import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { User } from "../lib/types";

interface AuthContextValue {
  user?: User;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadSession() {
      try {
        const account = await api.me();
        if (active) {
          setUser(account);
        }
      } catch (error: unknown) {
        if (!(error instanceof ApiError && error.status === 401)) {
          console.warn("Session check failed", error);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadSession();
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const account = await api.login(email, password);
    setUser(account);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(undefined);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [loading, signIn, signOut, user],
  );

  return <AuthContext value={contextValue}>{children}</AuthContext>;
}

export function useAuth() {
  const context = use(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
